import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { OrgSummary } from '@aiai/shared';
import { fetchOrgs } from '../api';
import { getActiveOrgId, setActiveOrgId, setActiveOrgSlug } from '../lib/activeOrg';
import { useAuth } from '../auth/useAuth';
import { Spinner } from '../components/Spinner';

export interface OrgContextValue {
  orgs: OrgSummary[];
  activeOrg: OrgSummary | null;
  setActiveOrg: (id: string) => void;
  isLoading: boolean;
}

// The context object lives here beside its provider; `useOrg` consumes it. The
// non-component export trips react-refresh's heuristic, which is harmless here.
// eslint-disable-next-line react-refresh/only-export-components
export const OrgContext = createContext<OrgContextValue | null>(null);

/**
 * Resolves and exposes the caller's active organization. Sits just inside
 * AuthProvider (so it only ever queries `/orgs` once authenticated) and
 * outside the routed app, so every org-scoped request already carries
 * `X-Org-Id` (attached by the interceptor in lib/api.ts, driven by
 * lib/activeOrg.ts) before any page mounts.
 */
export function OrgProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const [activeOrgId, setActiveOrgIdState] = useState<string | null>(() =>
    getActiveOrgId(),
  );

  const { data: orgs = [], isLoading, isFetched } = useQuery({
    queryKey: ['orgs'],
    queryFn: fetchOrgs,
    enabled: isAuthenticated,
    staleTime: 30_000,
  });

  // No auto-provisioning: a workspace is a deliberately-provisioned thing (an
  // admin creates one and invites people), not a freebie. A user with zero
  // workspaces is handled by routing (RootRedirect -> NoWorkspacePage).

  // Resolve (or repair) the active org whenever the list changes: keep the
  // persisted choice if it's still in the list, otherwise fall back to the
  // first org. Also covers the case where the persisted org was removed.
  useEffect(() => {
    if (!isAuthenticated || !isFetched || orgs.length === 0) return;
    const persisted = getActiveOrgId();
    const resolvedOrg =
      (persisted && orgs.find((o) => o.id === persisted)) || orgs[0];
    const resolved = resolvedOrg.id;
    if (resolved !== getActiveOrgId()) {
      setActiveOrgId(resolved);
    }
    // Keep the slug (drives the `/w/<slug>` request prefix) in lockstep.
    setActiveOrgSlug(resolvedOrg.slug);
    setActiveOrgIdState(resolved);
  }, [isAuthenticated, isFetched, orgs]);

  const setActiveOrg = useCallback(
    (id: string) => {
      setActiveOrgId(id);
      setActiveOrgSlug(orgs.find((o) => o.id === id)?.slug ?? null);
      setActiveOrgIdState(id);
      // The org changed — every org-scoped query in the cache is now for the
      // wrong workspace, so refetch everything under the new /w/<slug>.
      void queryClient.invalidateQueries();
    },
    [queryClient, orgs],
  );

  const activeOrg = useMemo(
    () => orgs.find((o) => o.id === activeOrgId) ?? null,
    [orgs, activeOrgId],
  );

  const value = useMemo<OrgContextValue>(
    () => ({ orgs, activeOrg, setActiveOrg, isLoading }),
    [orgs, activeOrg, setActiveOrg, isLoading],
  );

  // Not authenticated (or session still restoring): org context doesn't
  // apply yet. Render children as-is — public routes (login, setup) never
  // call useOrg(), and ProtectedRoute gates everything that would.
  if (!isAuthenticated) {
    return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
  }

  // Authenticated but the workspace list is still loading — block so no page
  // mounts before we know the active workspace (drives X-Org-Id / the slug
  // prefix). Once loaded, a user with workspaces but a not-yet-resolved active
  // one holds for a render; a user with ZERO workspaces renders through so
  // routing can show the "no workspace" screen.
  if (!isFetched || (orgs.length > 0 && !activeOrg)) {
    return (
      <div className="grid min-h-screen place-items-center text-ink-muted">
        <Spinner />
      </div>
    );
  }

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}
